/**
 * Policies API - CRUD operations for dg_policies
 */

import apiClient from '../services/api';

const ADMIN_TOKEN = 'dev';

/**
 * List policies for a brand
 * @param {string} brandId - Brand ID
 * @param {object} options - Optional filters
 * @param {string} options.policyKey - Filter by policy key
 * @param {string} options.status - Filter by status (active/deprecated)
 * @returns {Promise<{ok: boolean, policies: Array}>}
 */
export async function listPolicies(brandId, { policyKey, status } = {}) {
  const params = new URLSearchParams({ brand_id: brandId });
  if (policyKey) params.set('policy_key', policyKey);
  if (status) params.set('status', status);
  
  const response = await apiClient.get(`/api/v1/decision-graph/policies?${params.toString()}`, {
    headers: { 'X-Admin-Token': ADMIN_TOKEN }
  });
  return response.data;
}

/**
 * Create a new policy version
 * @param {object} policy - Policy data
 * @param {string} policy.brand_id - Brand ID
 * @param {string} policy.policy_key - Policy key
 * @param {string} policy.version - Version string
 * @param {string} policy.status - Status (active/deprecated)
 * @param {object} policy.content - Policy content
 * @returns {Promise<{ok: boolean, policy_id: string}>}
 */
export async function createPolicy(policy) {
  const response = await apiClient.post('/api/v1/decision-graph/policies', policy, {
    headers: { 'X-Admin-Token': ADMIN_TOKEN }
  });
  return response.data;
}

/**
 * Update a policy
 * @param {string} policyId - Policy ID
 * @param {object} updates - Updates to apply
 * @param {string} updates.status - New status
 * @param {object} updates.content - New content
 * @returns {Promise<{ok: boolean, policy: object}>}
 */
export async function updatePolicy(policyId, updates) {
  const response = await apiClient.patch(`/api/v1/decision-graph/policies/${encodeURIComponent(policyId)}`, updates, {
    headers: { 'X-Admin-Token': ADMIN_TOKEN }
  });
  return response.data;
}

/**
 * Activate a specific policy version
 * @param {string} brandId - Brand ID
 * @param {string} policyKey - Policy key
 * @param {string} version - Version to activate
 * @returns {Promise<{ok: boolean, policy_id: string}>}
 */
export async function activatePolicy(brandId, policyKey, version) {
  const response = await apiClient.post('/api/v1/decision-graph/policies/activate', {
    brand_id: brandId,
    policy_key: policyKey,
    version
  }, {
    headers: { 'X-Admin-Token': ADMIN_TOKEN }
  });
  return response.data;
}

/**
 * Get policy configuration status for a brand
 * @param {string} brandId - Brand ID
 * @returns {Promise<{ok: boolean, configured: boolean, active_count: number, total_supported: number, active_versions: object, missing_keys: string[]}>}
 */
export async function getPoliciesStatus(brandId) {
  const response = await apiClient.get(`/api/v1/decision-graph/policies/status?brand_id=${encodeURIComponent(brandId)}`, {
    headers: { 'X-Admin-Token': ADMIN_TOKEN }
  });
  return response.data;
}

/**
 * Run the Policy Quick Start wizard
 * @param {object} payload - Quick start configuration
 * @param {string} payload.brand_id - Brand ID
 * @param {string} payload.preset - Preset name (balanced/customer_first/margin_first)
 * @param {string} payload.default_fulfillment - ship_complete or split_shipment
 * @param {number} payload.max_expedite_cost_usd - Max expedite cost
 * @param {number} payload.expedite_requires_approval_over_usd - Expedite approval threshold
 * @param {number} payload.max_refund_without_approval_usd - Max refund without approval
 * @param {number} payload.refund_requires_approval_over_usd - Refund approval threshold
 * @returns {Promise<{ok: boolean, brand_id: string, version: string, preset: string, keys: string[]}>}
 */
export async function quickstartPolicies(payload) {
  const response = await apiClient.post('/api/v1/decision-graph/policies/quickstart', payload, {
    headers: { 'X-Admin-Token': ADMIN_TOKEN }
  });
  return response.data;
}

/**
 * Get the Decision Pack for a trace
 * @param {string} brandId - Brand ID
 * @param {string} traceId - Trace ID
 * @returns {Promise<object>} Decision Pack response
 */
export async function getDecisionPack(brandId, traceId) {
  const response = await apiClient.get(
    `/api/v1/decision-graph/decision-pack?brand_id=${encodeURIComponent(brandId)}&trace_id=${encodeURIComponent(traceId)}`,
    { headers: { 'X-Admin-Token': ADMIN_TOKEN } }
  );
  return response.data;
}
